import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, ChevronLeft, Copy, Share2, ArrowRight,
  MapPin, Clock, IndianRupee, Sparkles,
} from 'lucide-react';
import { facilitiesAPI, sportsAPI, configurationsAPI, onboardingAPI } from '../services/api';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

// ─── constants ────────────────────────────────────────────────────────────────

const DAYS = [
  { key: 'mon', label: 'M' },
  { key: 'tue', label: 'T' },
  { key: 'wed', label: 'W' },
  { key: 'thu', label: 'T' },
  { key: 'fri', label: 'F' },
  { key: 'sat', label: 'S' },
  { key: 'sun', label: 'S' },
];
const ALL_DAYS = DAYS.map((d) => d.key);

const SLOT_PRESETS = [
  { label: '1 hr', minutes: 60 },
  { label: '2 hrs', minutes: 120 },
  { label: '3 hrs', minutes: 180 },
  { label: '4 hrs', minutes: 240 },
];

const PAYMENT_METHODS = ['Cash', 'UPI', 'Bank Transfer', 'Card'];

// Time-of-day bands for differential pricing. Boundaries in "HH:MM".
const TIME_BANDS = [
  { key: 'morning', label: 'Morning', icon: '🌅', from: '00:00', to: '12:00' },
  { key: 'afternoon', label: 'Afternoon', icon: '☀️', from: '12:00', to: '17:00' },
  { key: 'evening', label: 'Evening / Night', icon: '🌙', from: '17:00', to: '24:00' },
];

const toMins = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };

// Which bands actually contain slots given the day's open/close window.
function activeBands(openTime, closeTime) {
  const open = toMins(openTime), close = toMins(closeTime);
  return TIME_BANDS.filter((b) => toMins(b.from) < close && toMins(b.to) > open);
}

// Sport → suggested service cards shown on step 1
const SPORT_SERVICE_CARDS = [
  { id: 'cricket_ground', sportSlug: 'cricket', icon: '🏏', title: 'Cricket Ground', subtitle: 'Full ground for matches', defaultMinutes: 240 },
  { id: 'open_nets', sportSlug: 'cricket', icon: '🥅', title: 'Open Nets', subtitle: 'Hourly net practice', defaultMinutes: 60 },
  { id: 'practice_net', sportSlug: 'cricket', icon: '🎯', title: 'Practice Net', subtitle: 'Separate net structure', defaultMinutes: 60 },
  { id: 'box_cricket', sportSlug: 'cricket', icon: '📦', title: 'Box Cricket', subtitle: 'Box/cage cricket, hourly', defaultMinutes: 60 },
  { id: 'football_pitch', sportSlug: 'football', icon: '⚽', title: 'Football Pitch', subtitle: 'Full 5 or 7-a-side', defaultMinutes: 120 },
  { id: 'badminton_court', sportSlug: 'badminton', icon: '🏸', title: 'Badminton Court', subtitle: 'Indoor/outdoor court', defaultMinutes: 60 },
  { id: 'snooker_table', sportSlug: 'snooker', icon: '🎱', title: 'Snooker Table', subtitle: 'Per-table hourly booking', defaultMinutes: 60 },
  { id: 'custom', sportSlug: null, icon: '➕', title: 'Add Custom', subtitle: 'Define your own spot', defaultMinutes: 60 },
];

// Slot-duration chips for slot-booking services.
const DURATION_CHIPS = [60, 90, 120, 180, 240, 360, 480];

const fmtMins = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

// A fresh per-service config. Full grounds default to fixed-slot booking; nets
// and other hourly spaces default to per-hour booking.
const defaultServiceConfig = (card) => ({
  bookingMode: (card.id === 'cricket_ground' || card.id === 'football_pitch') ? 'slot' : 'hourly',
  openTime: '06:00',
  closeTime: '22:00',
  slotMinutes: card.defaultMinutes || 60,
  gapMinutes: 0,
  days: ALL_DAYS,
  base: { weekday: '', weekend: '' },
  byTime: false,   // hourly: vary price by time-of-day band
  bands: {},
  perSlot: false,  // slot: price each slot/day individually (matrix)
  slotDayPrices: {}, // { [slotIndex]: { mon, tue, wed, thu, fri, sat, sun } }
});

// Day columns for the per-slot price matrix (slot services).
const DAY_COLS = [
  { key: 'mon', label: 'Mon', num: 1, weekend: false },
  { key: 'tue', label: 'Tue', num: 2, weekend: false },
  { key: 'wed', label: 'Wed', num: 3, weekend: false },
  { key: 'thu', label: 'Thu', num: 4, weekend: false },
  { key: 'fri', label: 'Fri', num: 5, weekend: false },
  { key: 'sat', label: 'Sat', num: 6, weekend: true },
  { key: 'sun', label: 'Sun', num: 0, weekend: true },
];

// Generate a service's daily slots from its config (duration + gap).
function genSlotsFor(cfg) {
  if (!cfg) return [];
  const open = toMins(cfg.openTime);
  const close = toMins(cfg.closeTime);
  const dur = cfg.bookingMode === 'hourly' ? 60 : (Number(cfg.slotMinutes) || 60);
  const gap = Number(cfg.gapMinutes) || 0;
  const step = dur + gap;
  const out = [];
  if (close > open && dur > 0) {
    for (let c = open; c + dur <= close && out.length < 60; c += step) {
      out.push({ start: fmtMins(c), end: fmtMins(c + dur), index: out.length });
    }
  }
  return out;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function ProgressDots({ total, current }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`rounded-full transition-all ${
            i < current
              ? 'h-2 w-2 bg-ga-primary'
              : i === current
              ? 'h-2 w-5 bg-ga-primary'
              : 'h-2 w-2 bg-ga-border-subtle'
          }`}
        />
      ))}
    </div>
  );
}

function WizardShell({ step, totalSteps, onBack, title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-ga-bg-page flex flex-col">
      {/* top bar */}
      <div className="sticky top-0 z-10 bg-ga-bg-page px-4 pt-safe-top">
        <div className="mx-auto max-w-lg">
          <div className="flex items-center justify-between py-4">
            {onBack ? (
              <button onClick={onBack} className="flex items-center gap-1 text-sm text-ga-text-muted hover:text-ga-text-primary">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <Logo size="sm" />
            )}
            <ProgressDots total={totalSteps} current={step} />
            <span className="text-xs text-ga-text-muted">{step + 1} of {totalSteps}</span>
          </div>
        </div>
      </div>

      {/* content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg px-4 pb-32">
          <div className="mt-2 mb-6">
            <h1 className="text-2xl font-bold text-ga-text-primary leading-tight">{title}</h1>
            {subtitle && <p className="mt-1.5 text-sm text-ga-text-muted">{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>

      {/* sticky footer */}
      {footer && (
        <div className="fixed bottom-0 left-0 right-0 bg-ga-bg-page border-t border-ga-border px-4 pb-safe-bottom pt-3">
          <div className="mx-auto max-w-lg">
            {footer}
          </div>
        </div>
      )}
    </div>
  );
}

function PriceInput({ value, onChange, compact = false }) {
  return (
    <div className={`flex items-center gap-1 rounded-xl border border-ga-border bg-ga-bg-surface-subtle ${compact ? 'px-2 py-1.5 flex-1' : 'px-3 py-2'}`}>
      <IndianRupee className="h-3.5 w-3.5 text-ga-text-muted shrink-0" />
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className={`bg-transparent text-sm font-semibold text-ga-text-primary focus:outline-none ${compact ? 'w-full min-w-0' : 'w-20'}`}
      />
    </div>
  );
}

function PrimaryBtn({ onClick, disabled, loading, children, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full rounded-xl bg-ga-primary py-3.5 text-sm font-semibold text-ga-text-on-primary transition hover:bg-ga-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 ${className}`}
    >
      {loading ? (
        <span className="h-4 w-4 rounded-full border-2 border-ga-text-on-primary/30 border-t-ga-text-on-primary animate-spin" />
      ) : children}
    </button>
  );
}

// ─── Step 1: Service type selection ───────────────────────────────────────────

function StepSetup({ selected, onToggle, onContinue, totalSteps = 5 }) {
  const sharesGround = selected.includes('cricket_ground') && selected.includes('open_nets');

  return (
    <WizardShell
      step={0}
      totalSteps={totalSteps}
      title="What can be booked at your ground?"
      subtitle="Select all that apply. You can add more later."
      footer={
        <div className="space-y-2">
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selected.map((id) => {
                const card = SPORT_SERVICE_CARDS.find((c) => c.id === id);
                return (
                  <span key={id} className="flex items-center gap-1 rounded-full bg-ga-primary/10 px-2.5 py-1 text-xs font-medium text-ga-primary">
                    {card?.title}
                    <button onClick={() => onToggle(id)} className="ml-0.5 text-ga-primary/60 hover:text-ga-primary">×</button>
                  </span>
                );
              })}
            </div>
          )}
          {sharesGround && (
            <div className="mb-2 flex items-start gap-2 rounded-xl border border-ga-warning/30 bg-ga-warning/10 px-3 py-2.5 text-xs text-ga-warning">
              <span className="text-base leading-none">⚠️</span>
              <span>These will share the main ground — bookings will be automatically exclusive.</span>
            </div>
          )}
          <PrimaryBtn onClick={onContinue} disabled={selected.length === 0}>
            Continue <ArrowRight className="h-4 w-4" />
          </PrimaryBtn>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        {SPORT_SERVICE_CARDS.map((card) => {
          const isSelected = selected.includes(card.id);
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => onToggle(card.id)}
              className={`relative flex flex-col items-start gap-1 rounded-2xl border-2 p-4 text-left transition-all ${
                isSelected
                  ? 'border-ga-primary bg-ga-primary/5'
                  : 'border-ga-border bg-ga-bg-surface hover:border-ga-primary/40'
              }`}
            >
              {isSelected && (
                <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-ga-primary">
                  <Check className="h-3 w-3 text-ga-text-on-primary" />
                </span>
              )}
              <span className="text-2xl leading-none">{card.icon}</span>
              <span className="mt-1 text-sm font-semibold text-ga-text-primary">{card.title}</span>
              <span className="text-xs text-ga-text-muted">{card.subtitle}</span>
            </button>
          );
        })}
      </div>
    </WizardShell>
  );
}

// ─── Step 2: Facility details ─────────────────────────────────────────────────

function StepFacility({ groundName, setGroundName, location, setLocation, sports, selectedSports, toggleSport, onBack, onContinue, saving, totalSteps = 5 }) {
  const handleLocate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, address: 'Current location' }),
      () => {}
    );
  };

  return (
    <WizardShell
      step={1}
      totalSteps={totalSteps}
      onBack={onBack}
      title="Tell us about your ground"
      subtitle="This helps customers find and book you."
      footer={
        <PrimaryBtn onClick={onContinue} disabled={!groundName.trim()} loading={saving}>
          Continue <ArrowRight className="h-4 w-4" />
        </PrimaryBtn>
      }
    >
      <div className="space-y-5">
        {/* Ground name */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ga-text-primary">Ground name</label>
          <input
            type="text"
            value={groundName}
            onChange={(e) => setGroundName(e.target.value)}
            placeholder="e.g. Tyagi Sports Ground"
            className="w-full rounded-xl border border-ga-border bg-ga-bg-surface px-4 py-3 text-sm text-ga-text-primary placeholder:text-ga-text-muted focus:border-ga-primary focus:outline-none"
          />
        </div>

        {/* Location */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ga-text-primary">Location</label>
          <button
            type="button"
            onClick={handleLocate}
            className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-sm text-left transition ${
              location
                ? 'border-ga-primary bg-ga-primary/5 text-ga-text-primary'
                : 'border-dashed border-ga-border bg-ga-bg-surface text-ga-text-muted hover:border-ga-primary/40'
            }`}
          >
            <MapPin className={`h-4 w-4 shrink-0 ${location ? 'text-ga-primary' : 'text-ga-text-muted'}`} />
            {location ? (
              <span>{location.address || `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`}</span>
            ) : (
              <span>Tap to use current location</span>
            )}
            {location && <Check className="ml-auto h-4 w-4 text-ga-primary" />}
          </button>
          {location && (
            <p className="mt-1.5 text-xs text-ga-text-muted">
              Lat {location.lat?.toFixed(5)}, Lng {location.lng?.toFixed(5)}
            </p>
          )}
        </div>

        {/* Sport type */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ga-text-primary">Sport type</label>
          <div className="flex flex-wrap gap-2">
            {sports.map((s) => {
              const on = selectedSports.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSport(s.id)}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition ${
                    on
                      ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                      : 'border-ga-border bg-ga-bg-surface text-ga-text-primary hover:border-ga-primary/50'
                  }`}
                >
                  {s.icon} {s.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </WizardShell>
  );
}

// ─── Step 3: Timings (per service) ────────────────────────────────────────────

function ServiceTimingCard({ card, cfg, update, toggleDay }) {
  const slots = genSlotsFor(cfg);
  const isHourly = cfg.bookingMode === 'hourly';
  return (
    <div className="rounded-2xl border border-ga-border bg-ga-bg-surface p-4 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-2xl leading-none">{card.icon}</span>
        <p className="font-semibold text-ga-text-primary">{card.title}</p>
      </div>

      {/* Booking type */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { id: 'slot', label: 'Slot booking', desc: 'Fixed-length blocks' },
          { id: 'hourly', label: 'Hourly booking', desc: 'Booked per hour' },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => update({ bookingMode: m.id })}
            className={`rounded-xl border-2 p-2.5 text-left transition-colors ${
              cfg.bookingMode === m.id
                ? 'border-ga-primary bg-ga-primary/5'
                : 'border-ga-border-subtle hover:border-ga-primary/40'
            }`}
          >
            <div className="text-sm font-semibold text-ga-text-primary">{m.label}</div>
            <div className="text-xs text-ga-text-muted">{m.desc}</div>
          </button>
        ))}
      </div>

      {/* Open / Close */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Opens', key: 'openTime' },
          { label: 'Closes', key: 'closeTime' },
        ].map(({ label, key }) => (
          <label key={key} className="flex items-center justify-between rounded-xl border border-ga-border bg-ga-bg-surface-subtle px-3 py-2.5">
            <span className="text-sm text-ga-text-muted">{label}</span>
            <input
              type="time"
              value={cfg[key]}
              onChange={(e) => update({ [key]: e.target.value })}
              className="bg-transparent text-sm font-medium text-ga-text-primary focus:outline-none"
            />
          </label>
        ))}
      </div>

      {/* Days */}
      <div>
        <label className="mb-2 block text-xs font-medium text-ga-text-muted">Days open</label>
        <div className="flex gap-1.5">
          {DAYS.map((d) => {
            const on = cfg.days.includes(d.key);
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => toggleDay(d.key)}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition ${
                  on ? 'bg-ga-primary text-ga-text-on-primary' : 'bg-ga-bg-surface-subtle border border-ga-border text-ga-text-muted'
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot duration (slot mode only) */}
      {!isHourly && (
        <div>
          <label className="mb-2 block text-xs font-medium text-ga-text-muted">Slot duration</label>
          <div className="flex flex-wrap gap-2">
            {DURATION_CHIPS.map((mins) => (
              <button
                key={mins}
                type="button"
                onClick={() => update({ slotMinutes: mins })}
                className={`rounded-full px-3 py-1.5 text-sm font-medium border transition ${
                  Number(cfg.slotMinutes) === mins
                    ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                    : 'border-ga-border bg-ga-bg-surface-subtle text-ga-text-primary'
                }`}
              >
                {mins % 60 === 0 ? `${mins / 60} hr${mins > 60 ? 's' : ''}` : `${mins} min`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gap between slots */}
      <div>
        <label className="mb-1.5 block text-xs font-medium text-ga-text-muted">Gap between slots (minutes)</label>
        <input
          type="number"
          min={0}
          max={120}
          value={cfg.gapMinutes || ''}
          onChange={(e) => update({ gapMinutes: e.target.value })}
          placeholder="0"
          className="w-28 rounded-xl border border-ga-border bg-ga-bg-surface-subtle px-3 py-2 text-sm text-ga-text-primary focus:outline-none"
        />
        <span className="ml-2 text-xs text-ga-text-muted">e.g. cleanup / changeover time</span>
      </div>

      {/* Preview */}
      <div className="rounded-xl bg-ga-bg-surface-subtle px-3 py-2.5">
        <div className="flex items-center gap-2 text-sm font-medium text-ga-text-primary">
          <Clock className="h-4 w-4 text-ga-primary" />
          {slots.length} {isHourly ? 'hourly' : 'slot'}{slots.length === 1 ? '' : 's'}/day
        </div>
        {slots.length > 0 && (
          <p className="mt-1 text-xs text-ga-text-muted">
            {slots.slice(0, 5).map((s) => s.start).join(', ')}{slots.length > 5 ? ` +${slots.length - 5} more` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

function StepTimings({ services, svcConfigs, updateCfg, toggleCfgDay, onBack, onContinue, saving, totalSteps = 5 }) {
  const ready = services.every((c) => {
    const cfg = svcConfigs[c.id];
    return cfg && cfg.days.length > 0 && genSlotsFor(cfg).length > 0;
  });
  return (
    <WizardShell
      step={2}
      totalSteps={totalSteps}
      onBack={onBack}
      title="Set timings for each service"
      subtitle="Choose how each one is booked, when it's open, and slot length."
      footer={
        <PrimaryBtn onClick={onContinue} disabled={!ready} loading={saving}>
          Continue <ArrowRight className="h-4 w-4" />
        </PrimaryBtn>
      }
    >
      <div className="space-y-4">
        {services.map((card) => (
          svcConfigs[card.id] ? (
            <ServiceTimingCard
              key={card.id}
              card={card}
              cfg={svcConfigs[card.id]}
              update={(patch) => updateCfg(card.id, patch)}
              toggleDay={(d) => toggleCfgDay(card.id, d)}
            />
          ) : null
        ))}
      </div>
    </WizardShell>
  );
}

// ─── Pricing helpers ──────────────────────────────────────────────────────────

function PaymentMethods({ paymentMethods, togglePayment }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-ga-text-primary">How do you accept payments?</p>
      <div className="flex flex-wrap gap-2">
        {PAYMENT_METHODS.map((m) => {
          const on = paymentMethods.includes(m);
          return (
            <button
              key={m}
              type="button"
              onClick={() => togglePayment(m)}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition ${
                on ? 'border-ga-primary bg-ga-primary/10 text-ga-primary' : 'border-ga-border bg-ga-bg-surface text-ga-text-muted'
              }`}
            >
              {on && <Check className="h-3.5 w-3.5" />}
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PricingFooter({ monthlyRevenue, revenueLabel, disabled, saving, isLast, onContinue }) {
  return (
    <div className="space-y-2">
      {monthlyRevenue > 0 && (
        <div className="rounded-xl border border-ga-primary/20 bg-ga-primary/5 px-4 py-2.5 text-center">
          <p className="text-xs text-ga-text-muted">Est. monthly revenue · {revenueLabel}</p>
          <p className="text-lg font-bold text-ga-primary">₹{monthlyRevenue.toLocaleString('en-IN')}</p>
          <p className="text-[10px] text-ga-text-muted">assuming 60% occupancy</p>
        </div>
      )}
      <PrimaryBtn onClick={onContinue} disabled={disabled} loading={saving}>
        {isLast ? '🚀 Go Live!' : <>Continue <ArrowRight className="h-4 w-4" /></>}
      </PrimaryBtn>
    </div>
  );
}

const estRevenue = (services, svcConfigs) =>
  services.reduce((sum, card) => {
    const cfg = svcConfigs[card.id];
    if (!cfg) return sum;
    const weekday = parseFloat(cfg.base.weekday) || 0;
    const weekend = parseFloat(cfg.base.weekend) || weekday;
    const slotsPerDay = genSlotsFor(cfg).length;
    return sum + (weekday * slotsPerDay * 22) + (weekend * slotsPerDay * 8);
  }, 0);

// ─── Slot-based pricing page ──────────────────────────────────────────────────

function ServiceSlotPricingCard({ card, cfg, setBase, togglePerSlot, setSlotDayPrice }) {
  const slots = genSlotsFor(cfg);
  return (
    <div className="rounded-2xl border border-ga-border bg-ga-bg-surface p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-2xl leading-none">{card.icon}</span>
        <div>
          <p className="font-semibold text-ga-text-primary">{card.title}</p>
          <p className="text-xs text-ga-text-muted">{slots.length} slots/day · per slot</p>
        </div>
      </div>

      {/* Base price */}
      <div className="space-y-2.5">
        {[
          { key: 'weekday', label: 'Weekday price per slot' },
          { key: 'weekend', label: 'Weekend price per slot' },
        ].map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-ga-text-muted">{label}</span>
            <PriceInput value={cfg.base[key] || ''} onChange={(v) => setBase(key, v)} />
          </div>
        ))}
      </div>

      {/* Per-slot × per-day matrix */}
      <label className="mt-3 flex items-center justify-between cursor-pointer">
        <span className="text-sm font-medium text-ga-text-primary">Set price per slot &amp; day?</span>
        <div
          className={`relative h-6 w-11 rounded-full transition-colors ${cfg.perSlot ? 'bg-ga-primary' : 'bg-ga-border-subtle'}`}
          onClick={(e) => { e.preventDefault(); togglePerSlot(); }}
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-ga-bg-surface shadow transition-transform ${cfg.perSlot ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </div>
      </label>

      {cfg.perSlot && (
        <div className="mt-3 rounded-xl bg-ga-bg-surface-subtle p-3">
          <p className="mb-2 text-xs text-ga-text-muted">Blank cells use the base price. Scroll sideways for all days.</p>
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="border-separate" style={{ borderSpacing: '4px' }}>
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-ga-bg-surface-subtle text-left text-[11px] font-semibold text-ga-text-muted px-1">Slot</th>
                  {DAY_COLS.map((d) => (
                    <th key={d.key} className={`text-[11px] font-semibold px-1 ${d.weekend ? 'text-ga-primary' : 'text-ga-text-muted'}`}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((s, idx) => (
                  <tr key={idx}>
                    <td className="sticky left-0 z-10 bg-ga-bg-surface-subtle whitespace-nowrap pr-2 text-[11px] text-ga-text-primary">
                      <span className="font-semibold">S{idx + 1}</span>{' '}
                      <span className="text-ga-text-muted">{s.start}</span>
                    </td>
                    {DAY_COLS.map((d) => (
                      <td key={d.key}>
                        <input
                          type="number"
                          min={0}
                          value={cfg.slotDayPrices?.[idx]?.[d.key] || ''}
                          onChange={(e) => setSlotDayPrice(idx, d.key, e.target.value)}
                          placeholder={(d.weekend ? cfg.base.weekend : cfg.base.weekday) || '0'}
                          className="w-16 rounded-lg border border-ga-border bg-ga-bg-surface px-2 py-1.5 text-xs text-ga-text-primary placeholder:text-ga-text-muted/60 focus:border-ga-primary focus:outline-none"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StepSlotPricing({ services, svcConfigs, setCfgBase, toggleCfgPerSlot, setCfgSlotDayPrice, paymentMethods, togglePayment, onBack, onContinue, saving, dotIndex, dotTotal, isLast }) {
  const allPriced = services.every((card) => parseFloat(svcConfigs[card.id]?.base.weekday) > 0);
  return (
    <WizardShell
      step={dotIndex}
      totalSteps={dotTotal}
      onBack={onBack}
      title="Price your slot bookings"
      subtitle="Set a base price, then optionally fine-tune by slot and day of week."
      footer={
        <PricingFooter
          revenueLabel="slot bookings"
          monthlyRevenue={estRevenue(services, svcConfigs)}
          disabled={!allPriced || (isLast && paymentMethods.length === 0)}
          saving={saving}
          isLast={isLast}
          onContinue={onContinue}
        />
      }
    >
      <div className="space-y-4">
        {services.map((card) => (
          svcConfigs[card.id] ? (
            <ServiceSlotPricingCard
              key={card.id}
              card={card}
              cfg={svcConfigs[card.id]}
              setBase={(k, v) => setCfgBase(card.id, k, v)}
              togglePerSlot={() => toggleCfgPerSlot(card.id)}
              setSlotDayPrice={(i, d, v) => setCfgSlotDayPrice(card.id, i, d, v)}
            />
          ) : null
        ))}
        {isLast && <PaymentMethods paymentMethods={paymentMethods} togglePayment={togglePayment} />}
      </div>
    </WizardShell>
  );
}

// ─── Hourly pricing page ──────────────────────────────────────────────────────

function ServiceHourlyPricingCard({ card, cfg, setBase, toggleByTime, setBand }) {
  const bands = activeBands(cfg.openTime, cfg.closeTime);
  return (
    <div className="rounded-2xl border border-ga-border bg-ga-bg-surface p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-2xl leading-none">{card.icon}</span>
        <div>
          <p className="font-semibold text-ga-text-primary">{card.title}</p>
          <p className="text-xs text-ga-text-muted">Hourly · per hour</p>
        </div>
      </div>

      <div className="space-y-2.5">
        {[
          { key: 'weekday', label: 'Weekday price per hour' },
          { key: 'weekend', label: 'Weekend price per hour' },
        ].map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-ga-text-muted">{label}</span>
            <PriceInput value={cfg.base[key] || ''} onChange={(v) => setBase(key, v)} />
          </div>
        ))}
      </div>

      <label className="mt-3 flex items-center justify-between cursor-pointer">
        <span className="text-sm font-medium text-ga-text-primary">Different price by time of day?</span>
        <div
          className={`relative h-6 w-11 rounded-full transition-colors ${cfg.byTime ? 'bg-ga-primary' : 'bg-ga-border-subtle'}`}
          onClick={(e) => { e.preventDefault(); toggleByTime(); }}
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-ga-bg-surface shadow transition-transform ${cfg.byTime ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </div>
      </label>
      {cfg.byTime && (
        <div className="mt-3 space-y-3 rounded-xl bg-ga-bg-surface-subtle p-3">
          <p className="text-xs text-ga-text-muted">Leave blank to use the base price above.</p>
          {bands.map((band) => (
            <div key={band.key}>
              <p className="mb-1.5 text-xs font-semibold text-ga-text-primary">
                {band.icon} {band.label}{' '}
                <span className="font-normal text-ga-text-muted">({band.from}–{band.to === '24:00' ? cfg.closeTime : band.to})</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[{ key: 'weekday', label: 'Weekday' }, { key: 'weekend', label: 'Weekend' }].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between gap-1.5">
                    <span className="text-[11px] text-ga-text-muted shrink-0">{label}</span>
                    <PriceInput compact value={cfg.bands?.[band.key]?.[key] || ''} onChange={(v) => setBand(band.key, key, v)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepHourlyPricing({ services, svcConfigs, setCfgBase, toggleCfgByTime, setCfgBand, paymentMethods, togglePayment, onBack, onContinue, saving, dotIndex, dotTotal, isLast }) {
  const allPriced = services.every((card) => parseFloat(svcConfigs[card.id]?.base.weekday) > 0);
  return (
    <WizardShell
      step={dotIndex}
      totalSteps={dotTotal}
      onBack={onBack}
      title="Price your hourly bookings"
      subtitle="Set a per-hour price, with optional time-of-day rates."
      footer={
        <PricingFooter
          revenueLabel="hourly bookings"
          monthlyRevenue={estRevenue(services, svcConfigs)}
          disabled={!allPriced || (isLast && paymentMethods.length === 0)}
          saving={saving}
          isLast={isLast}
          onContinue={onContinue}
        />
      }
    >
      <div className="space-y-4">
        {services.map((card) => (
          svcConfigs[card.id] ? (
            <ServiceHourlyPricingCard
              key={card.id}
              card={card}
              cfg={svcConfigs[card.id]}
              setBase={(k, v) => setCfgBase(card.id, k, v)}
              toggleByTime={() => toggleCfgByTime(card.id)}
              setBand={(b, k, v) => setCfgBand(card.id, b, k, v)}
            />
          ) : null
        ))}
        {isLast && <PaymentMethods paymentMethods={paymentMethods} togglePayment={togglePayment} />}
      </div>
    </WizardShell>
  );
}

// ─── Step 5: Success ──────────────────────────────────────────────────────────

function StepSuccess({ facilitySlug, onGoToDashboard }) {
  const bookingUrl = `groundaale.com/${facilitySlug || 'your-ground'}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://${bookingUrl}`).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-ga-bg-page flex flex-col items-center justify-center px-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-ga-primary/20 ring-4 ring-ga-primary/30">
        <Check className="h-10 w-10 text-ga-primary" />
      </div>
      <h1 className="text-3xl font-bold text-ga-text-primary mb-2">You're live! 🎉</h1>
      <p className="text-ga-text-muted text-sm mb-8">
        {facilitySlug ? facilitySlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Your facility'} is now open for bookings.
      </p>

      {/* Booking link card */}
      <div className="w-full max-w-sm rounded-2xl border border-ga-border-subtle bg-ga-bg-surface p-5 mb-6 shadow-ga-soft">
        <p className="text-xs text-ga-text-subtle mb-2 uppercase tracking-wider">Your booking link</p>
        <p className="text-sm font-mono text-ga-text-primary bg-ga-bg-surface-subtle rounded-lg px-3 py-2 mb-3 text-left truncate">{bookingUrl}</p>
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-ga-border-subtle bg-ga-bg-surface-subtle py-2.5 text-sm font-medium text-ga-text-primary hover:bg-ga-bg-surface-subtle/70 transition"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-ga-primary py-2.5 text-sm font-medium text-ga-text-on-primary hover:bg-ga-primary/90 transition">
            <Share2 className="h-4 w-4" />
            Share via WhatsApp
          </button>
        </div>
        <p className="mt-3 text-xs text-ga-text-muted">Send this link to your existing customers to start getting bookings</p>
      </div>

      <div className="w-full max-w-sm space-y-2.5">
        <button
          onClick={() => window.open('/dashboard/book-now', '_blank')}
          className="w-full rounded-xl border border-ga-border-subtle bg-ga-bg-surface-subtle py-3.5 text-sm font-semibold text-ga-text-primary hover:bg-ga-bg-surface-subtle/70 transition"
        >
          View My Calendar →
        </button>
        <button
          onClick={onGoToDashboard}
          className="w-full rounded-xl bg-ga-primary py-3.5 text-sm font-semibold text-ga-text-on-primary hover:bg-ga-primary/90 transition"
        >
          Go to Dashboard
        </button>
        <p className="text-xs text-ga-text-muted">You can edit your setup anytime from Settings.</p>
      </div>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { activeFacilityId, allowedFacilities } = useFacility();
  const { refreshUser } = useAuth();

  const facilityId = activeFacilityId || allowedFacilities[0]?.facility_id;
  const facilityName = allowedFacilities.find((f) => f.facility_id === facilityId)?.facility_name
    || allowedFacilities[0]?.facility_name || '';
  const facilitySlug = facilityName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const STORAGE_KEY = facilityId ? `ga_onboarding_${facilityId}` : null;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [selectedCards, setSelectedCards] = useState([]);

  // Step 2 state
  const [sports, setSports] = useState([]);
  const [groundName, setGroundName] = useState('');
  const [location, setLocation] = useState(null);
  const [selectedSports, setSelectedSports] = useState([]);

  // Steps 3 & 4 — one timings+pricing config per selected service.
  const [svcConfigs, setSvcConfigs] = useState({});
  const [paymentMethods, setPaymentMethods] = useState(['Cash', 'UPI']);

  useEffect(() => {
    sportsAPI.getAll().then((res) => {
      if (res?.success) setSports(res.data || []);
    }).catch(() => {});
  }, []);

  // Resume an in-progress setup: nothing is written to the backend until the
  // final commit, so a half-finished journey is dumped to localStorage and
  // rehydrated here instead of restarting from step 1.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !STORAGE_KEY) return;
    hydrated.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (Array.isArray(s.selectedCards)) setSelectedCards(s.selectedCards);
      if (s.groundName) setGroundName(s.groundName);
      if (s.location) setLocation(s.location);
      if (Array.isArray(s.selectedSports)) setSelectedSports(s.selectedSports);
      if (s.svcConfigs) setSvcConfigs(s.svcConfigs);
      if (Array.isArray(s.paymentMethods)) setPaymentMethods(s.paymentMethods);
      if (typeof s.step === 'number') setStep(s.step);
    } catch { /* ignore corrupt draft */ }
  }, [STORAGE_KEY]);

  // Persist the draft on every change (until the journey completes at step 5).
  useEffect(() => {
    if (!STORAGE_KEY || !hydrated.current || step >= 5) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step, selectedCards, groundName, location, selectedSports, svcConfigs, paymentMethods,
      }));
    } catch { /* quota / serialization issues are non-fatal */ }
  }, [STORAGE_KEY, step, selectedCards, groundName, location, selectedSports, svcConfigs, paymentMethods]);

  const toggleCard = (id) => {
    setSelectedCards((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleSport = (id) => {
    setSelectedSports((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  // Step 1 → 2: carry the chosen services' sports forward so the sport-type
  // pills on the facility step come pre-selected (no need to re-pick).
  const goToFacilityStep = () => {
    const slugs = [
      ...new Set(
        selectedCards
          .map((id) => SPORT_SERVICE_CARDS.find((c) => c.id === id)?.sportSlug)
          .filter(Boolean)
      ),
    ];
    const sportIds = sports.filter((s) => slugs.includes(s.slug)).map((s) => s.id);
    setSelectedSports((prev) => Array.from(new Set([...prev, ...sportIds])));
    setStep(1);
  };

  const togglePayment = (m) => {
    setPaymentMethods((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  // ── Per-service config updaters ────────────────────────────────────────────
  const updateCfg = (id, patch) =>
    setSvcConfigs((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const toggleCfgDay = (id, key) =>
    setSvcConfigs((prev) => {
      const c = prev[id];
      const days = c.days.includes(key) ? c.days.filter((d) => d !== key) : [...c.days, key];
      return { ...prev, [id]: { ...c, days } };
    });

  const setCfgBase = (id, key, val) =>
    setSvcConfigs((prev) => ({ ...prev, [id]: { ...prev[id], base: { ...prev[id].base, [key]: val } } }));

  const toggleCfgByTime = (id) =>
    setSvcConfigs((prev) => ({ ...prev, [id]: { ...prev[id], byTime: !prev[id].byTime } }));

  const setCfgBand = (id, bandKey, key, val) =>
    setSvcConfigs((prev) => {
      const c = prev[id];
      const bands = c.bands || {};
      return { ...prev, [id]: { ...c, bands: { ...bands, [bandKey]: { ...(bands[bandKey] || {}), [key]: val } } } };
    });

  const toggleCfgPerSlot = (id) =>
    setSvcConfigs((prev) => ({ ...prev, [id]: { ...prev[id], perSlot: !prev[id].perSlot } }));

  const setCfgSlotDayPrice = (id, idx, dayKey, val) =>
    setSvcConfigs((prev) => {
      const c = prev[id];
      const sdp = c.slotDayPrices || {};
      return { ...prev, [id]: { ...c, slotDayPrices: { ...sdp, [idx]: { ...(sdp[idx] || {}), [dayKey]: val } } } };
    });

  const DAY_MAP = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };

  // True if any chosen service uses this booking mode.
  const modePresent = (mode) =>
    selectedCards.some((id) => svcConfigs[id]?.bookingMode === mode);

  // ── Navigation only (NO DB writes until the final commit) ──────────────────
  // Nothing is persisted to the backend mid-journey, so partial setups never
  // create half-built grounds, duplicate services or stray slot rules.

  const goToTimings = () => {
    if (!groundName.trim()) { setError('Please enter a ground name.'); return; }
    setError('');
    setStep(2);
  };

  const goToPricing = () => {
    setError('');
    setStep(modePresent('slot') ? 3 : modePresent('hourly') ? 4 : 5);
  };

  // Build the price_rules array for a service config (both booking modes).
  const buildPriceRules = (cfg) => {
    const WEEKDAYS = [1, 2, 3, 4, 5];
    const WEEKENDS = [0, 6];
    const weekdayPrice = parseFloat(cfg.base.weekday) || 0;
    const weekendPrice = parseFloat(cfg.base.weekend) || weekdayPrice;
    const rules = [];
    if (weekendPrice !== weekdayPrice) {
      rules.push({ days_of_week: WEEKENDS, slot_position: null, time_from: null, time_to: null, price: weekendPrice, price_unit: 'per_slot' });
    }
    if (cfg.bookingMode === 'hourly' && cfg.byTime) {
      for (const band of activeBands(cfg.openTime, cfg.closeTime)) {
        const b = cfg.bands?.[band.key] || {};
        const bWeekday = parseFloat(b.weekday);
        const bWeekend = parseFloat(b.weekend);
        const fromTime = band.from === '00:00' ? null : band.from;
        const toTime = band.to === '24:00' ? null : band.to;
        if (bWeekday > 0) rules.push({ days_of_week: WEEKDAYS, slot_position: null, time_from: fromTime, time_to: toTime, price: bWeekday, price_unit: 'per_slot' });
        if (bWeekend > 0) rules.push({ days_of_week: WEEKENDS, slot_position: null, time_from: fromTime, time_to: toTime, price: bWeekend, price_unit: 'per_slot' });
      }
    } else if (cfg.bookingMode === 'slot' && cfg.perSlot) {
      genSlotsFor(cfg).forEach((s, idx) => {
        DAY_COLS.forEach((d) => {
          const v = parseFloat(cfg.slotDayPrices?.[idx]?.[d.key]);
          if (v > 0) rules.push({ days_of_week: [d.num], slot_position: idx + 1, time_from: null, time_to: null, price: v, price_unit: 'per_slot' });
        });
      });
    }
    return { rules, basePrice: weekdayPrice };
  };

  // Final commit (Go Live): writes everything to the backend ONCE. Idempotent —
  // reuses the existing ground/services by name so a retry can't duplicate them.
  const commitAll = useCallback(async () => {
    if (!facilityId) return;
    setSaving(true);
    setError('');
    try {
      // 1. Ground — rename the facility's existing ground or create one.
      const groundsRes = await facilitiesAPI.getGrounds();
      const existingGrounds = (groundsRes?.data || []).filter((g) => g.facility_id === facilityId);
      let groundId;
      if (existingGrounds.length > 0) {
        await facilitiesAPI.renameGround(existingGrounds[0].id, groundName);
        groundId = existingGrounds[0].id;
      } else {
        const addRes = await facilitiesAPI.addGround(groundName);
        groundId = addRes?.data?.id;
      }

      // 2. Location.
      if (location) {
        await facilitiesAPI.updateLocation(facilityId, {
          latitude: location.lat,
          longitude: location.lng,
          address: location.address || '',
        });
      }

      // 3. Existing services on this ground, keyed by name → no duplicates.
      const svcRes = await facilitiesAPI.getServices(null, facilityId);
      const svcList = svcRes?.success ? (svcRes.data || []) : (Array.isArray(svcRes) ? svcRes : []);
      const byName = new Map(
        svcList.filter((s) => Number(s.ground_id) === Number(groundId)).map((s) => [s.name, s.id])
      );

      // 4. Create each service (if missing) and save its slot rule + prices.
      const cards = SPORT_SERVICE_CARDS.filter((c) => selectedCards.includes(c.id) && c.id !== "custom");
      for (const card of cards) {
        const cfg = svcConfigs[card.id];
        if (!cfg) continue;
        const sport = sports.find((s) => s.slug === card.sportSlug);
        const isCricketGround = card.id === 'cricket_ground';
        const serviceType = isCricketGround
          ? 'cricket_match'
          : card.id === 'open_nets' || card.id === 'practice_net'
          ? 'practice_net'
          : 'individual';

        let serviceId = byName.get(card.title);
        if (!serviceId) {
          const res = await facilitiesAPI.createService(facilityId, {
            ground_id: groundId,
            name: card.title,
            type: serviceType,
            category: isCricketGround ? 'team_sports' : 'individual_sports',
            pricing_model: 'slot',
            sport_id: sport?.id || null,
          });
          serviceId = res?.data?.id;
        }
        if (!serviceId) continue;

        const dur = cfg.bookingMode === 'hourly' ? 60 : (Number(cfg.slotMinutes) || 60);
        const { rules, basePrice } = buildPriceRules(cfg);
        await configurationsAPI.saveSlotRule({
          service_id: serviceId,
          days_of_week: cfg.days.map((d) => DAY_MAP[d]).sort(),
          open_time: cfg.openTime,
          close_time: cfg.closeTime,
          slot_minutes: dur,
          break_minutes: Number(cfg.gapMinutes) || 0,
          price: basePrice,
          price_unit: 'per_slot',
          price_rules: rules,
        });
      }

      // 5. Advance the facility out of setup_incomplete (→ trial) and refresh
      // the session so FacilityContext.facilityStatus updates — otherwise the
      // dashboard would route the (still setup_incomplete) facility straight
      // back into this wizard.
      await onboardingAPI.complete();
      if (refreshUser) await refreshUser();

      // 6. Done — clear the saved draft and celebrate.
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      setStep(5);
    } catch (e) {
      setError(e.message || 'Could not finish setup. Please try Go Live again.');
    } finally {
      setSaving(false);
    }
  }, [facilityId, groundName, location, selectedCards, svcConfigs, sports, STORAGE_KEY, refreshUser]);

  // A pricing page's primary action: commit on the last one, else just advance.
  const onPricingContinue = (nextStep, isLast) => () => {
    if (isLast) return commitAll();
    setStep(nextStep);
  };

  const selectedCardObjects = SPORT_SERVICE_CARDS.filter((c) => selectedCards.includes(c.id) && c.id !== 'custom');

  // Split services by booking mode → drives the two pricing pages and the dots.
  const slotServices = selectedCardObjects.filter((c) => svcConfigs[c.id]?.bookingMode === 'slot');
  const hourlyServices = selectedCardObjects.filter((c) => svcConfigs[c.id]?.bookingMode === 'hourly');
  const hasSlot = slotServices.length > 0;
  const hasHourly = hourlyServices.length > 0;
  const dotTotal = 4 + Math.max(1, (hasSlot ? 1 : 0) + (hasHourly ? 1 : 0));

  // Seed a default timings+pricing config for each newly chosen service.
  useEffect(() => {
    setSvcConfigs((prev) => {
      const next = { ...prev };
      selectedCardObjects.forEach((card) => {
        if (!next[card.id]) next[card.id] = defaultServiceConfig(card);
      });
      return next;
    });
  }, [selectedCards]);

  return (
    <>
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full mx-4 rounded-xl border border-ga-danger/30 bg-ga-danger/10 px-4 py-3 text-sm text-ga-danger shadow-ga-soft flex items-center justify-between gap-2">
          {error}
          <button onClick={() => setError('')} className="text-ga-danger/70 hover:text-ga-danger">×</button>
        </div>
      )}

      {step === 0 && (
        <StepSetup
          selected={selectedCards}
          onToggle={toggleCard}
          onContinue={goToFacilityStep}
          totalSteps={dotTotal}
        />
      )}

      {step === 1 && (
        <StepFacility
          groundName={groundName}
          setGroundName={setGroundName}
          location={location}
          setLocation={setLocation}
          sports={sports}
          selectedSports={selectedSports}
          toggleSport={toggleSport}
          onBack={() => setStep(0)}
          onContinue={goToTimings}
          saving={saving}
          totalSteps={dotTotal}
        />
      )}

      {step === 2 && (
        <StepTimings
          services={selectedCardObjects}
          svcConfigs={svcConfigs}
          updateCfg={updateCfg}
          toggleCfgDay={toggleCfgDay}
          onBack={() => setStep(1)}
          onContinue={goToPricing}
          saving={saving}
          totalSteps={dotTotal}
        />
      )}

      {step === 3 && (
        <StepSlotPricing
          services={slotServices}
          svcConfigs={svcConfigs}
          setCfgBase={setCfgBase}
          toggleCfgPerSlot={toggleCfgPerSlot}
          setCfgSlotDayPrice={setCfgSlotDayPrice}
          paymentMethods={paymentMethods}
          togglePayment={togglePayment}
          onBack={() => setStep(2)}
          onContinue={onPricingContinue(hasHourly ? 4 : 5, !hasHourly)}
          saving={saving}
          dotIndex={3}
          dotTotal={dotTotal}
          isLast={!hasHourly}
        />
      )}

      {step === 4 && (
        <StepHourlyPricing
          services={hourlyServices}
          svcConfigs={svcConfigs}
          setCfgBase={setCfgBase}
          toggleCfgByTime={toggleCfgByTime}
          setCfgBand={setCfgBand}
          paymentMethods={paymentMethods}
          togglePayment={togglePayment}
          onBack={() => setStep(hasSlot ? 3 : 2)}
          onContinue={onPricingContinue(5, true)}
          saving={saving}
          dotIndex={hasSlot ? 4 : 3}
          dotTotal={dotTotal}
          isLast
        />
      )}

      {step === 5 && (
        <StepSuccess
          facilitySlug={facilitySlug}
          onGoToDashboard={() => navigate('/dashboard')}
        />
      )}
    </>
  );
}
